import Phaser from 'phaser';
import { CoffeeMakingGame } from '../minigames/CoffeeMakingGame.js';
import { drawFirstPersonWorkspace, drawCustomerFirstPerson } from './CoffeeShopSceneFirstPerson.js';
import portraits from '../portraits/portraitAssets.js';

export class CoffeeShopScene extends Phaser.Scene {
  constructor() {
    super({ key: 'CoffeeShopScene' });
    
    this.gameState = {
      currentDay: 1,
      currentYear: 2045,
      timeOfDay: 'morning',
      act: 1,
      money: 0,
      totalCoffeesMade: 0
    };
    
    this.npcsAtCounter = [];
    this.selectedNpcIndex = 0;
    this.npcQueue = [];
    this.nextSpawnTime = 0;
  }
  
  preload() {
    console.log('CoffeeShopScene.preload() called');
    
    // Load character portraits using Vite-processed URLs
    const characters = ['maya', 'james', 'alex', 'sam', 'jordan'];
    const expressions = ['neutral', 'happy', 'surprised'];
    
    console.log('[Preload] Loading character portraits with Vite URLs...');
    characters.forEach(char => {
      expressions.forEach(expr => {
        const key = `portrait-${char}-${expr}`;
        const url = portraits[char][expr];
        console.log(`[Preload] Loading: ${key} from ${url}`);
        this.load.image(key, url);
      });
    });
    
    // Add load error handlers
    this.load.on('loaderror', (fileObj) => {
      console.error('[Preload] Failed to load:', fileObj.key, fileObj.src);
    });
    
    this.load.on('filecomplete', (key) => {
      if (key.startsWith('portrait-')) {
        console.log('[Preload] ✓ Loaded:', key);
      }
    });
    
    // Generate pixel art textures
    this.createPixelTextures();
  }
  
  createPixelTextures() {
    // Wood plank texture
    const woodCanvas = this.textures.createCanvas('wood', 64, 64);
    const woodCtx = woodCanvas.context;
    woodCtx.fillStyle = '#8B5A2B';
    woodCtx.fillRect(0, 0, 64, 64);
    // Wood grain
    woodCtx.fillStyle = '#7A4A1B';
    for (let i = 0; i < 8; i++) {
      woodCtx.fillRect(0, i * 8 + 2, 64, 2);
    }
    woodCtx.fillStyle = '#9B6A3B';
    for (let i = 0; i < 8; i++) {
      woodCtx.fillRect(0, i * 8 + 5, 64, 1);
    }
    woodCanvas.refresh();
    
    // Brick texture
    const brickCanvas = this.textures.createCanvas('brick', 64, 32);
    const brickCtx = brickCanvas.context;
    brickCtx.fillStyle = '#8B4513';
    brickCtx.fillRect(0, 0, 64, 32);
    brickCtx.fillStyle = '#6B3510';
    brickCtx.fillRect(0, 0, 30, 14);
    brickCtx.fillRect(32, 0, 30, 14);
    brickCtx.fillRect(16, 16, 30, 14);
    brickCtx.fillStyle = '#5B2500';
    brickCtx.fillRect(0, 14, 64, 2);
    brickCtx.fillRect(0, 30, 64, 2);
    brickCtx.fillRect(30, 0, 2, 16);
    brickCtx.fillRect(14, 16, 2, 16);
    brickCtx.fillRect(46, 16, 2, 16);
    brickCanvas.refresh();
    
    // Floor tile texture
    const tileCanvas = this.textures.createCanvas('tile', 32, 32);
    const tileCtx = tileCanvas.context;
    tileCtx.fillStyle = '#3D2914';
    tileCtx.fillRect(0, 0, 32, 32);
    tileCtx.fillStyle = '#4D3924';
    tileCtx.fillRect(1, 1, 30, 30);
    tileCtx.fillStyle = '#2D1904';
    tileCtx.fillRect(0, 0, 32, 1);
    tileCtx.fillRect(0, 0, 1, 32);
    tileCanvas.refresh();
  }
  
  create() {
    // Create visible debug log on screen
    this.createDebugLog('CoffeeShopScene.create() START - player: ' + window.playerName);
    
    // Debug: Log all loaded textures
    console.log('[Create] All loaded textures:', this.textures.getTextureKeys().filter(k => k.startsWith('portrait')));
    
    // Set up dialogue manager with this scene for portrait support
    if (window.dialogueManager) {
      window.dialogueManager.setScene(this);
    }
    
    // Expose functions for mobile controls
    window.gameTalkToCustomer = () => this.talkToSelectedCustomer();
    window.gameMakeCoffee = () => this.startCoffeeMaking();
    
    try {
      this.logDebug('Drawing first-person workspace...');
      drawFirstPersonWorkspace(this);
      this.logDebug('Workspace drawn ✓');
      
      this.logDebug('Creating coffee game...');
      this.coffeeGame = new CoffeeMakingGame(this);
      this.coffeeGame.onComplete = (result) => this.onCoffeeComplete(result);
      this.logDebug('Coffee game created ✓');
      
      this.logDebug('Setting up controls...');
      this.leftKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.LEFT);
      this.rightKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.RIGHT);
      this.spaceKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
      this.cKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.C);
      this.escKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ESC);
      this.input.keyboard.removeCapture(Phaser.Input.Keyboard.KeyCodes.SPACE);
      this.logDebug('Controls set up ✓');
      
      this.logDebug('Creating UI...');
      this.createUI();
      this.logDebug('UI created ✓');
      
      this.nextSpawnTime = this.time.now + 2000;
      
      this.logDebug('Creating selection arrow...');
      this.selectionArrow = this.add.text(0, 0, '▼', {
        fontSize: '20px',
        fill: '#FFD700',
        stroke: '#000',
        strokeThickness: 3
      }).setOrigin(0.5).setVisible(false).setDepth(100);
      
      this.tweens.add({
        targets: this.selectionArrow,
        y: '+=8',
        duration: 400,
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
      this.logDebug('Selection arrow created ✓');
      
      this.logDebug('Creating ambient effects...');
      this.createAmbientEffects();
      this.logDebug('Ambient effects created ✓');
      
      this.logDebug('CoffeeShopScene.create() COMPLETE ✓✓✓');
      
      // Remove debug log after 5 seconds
      this.time.delayedCall(5000, () => {
        if (this.debugLogEl) this.debugLogEl.style.display = 'none';
      });
    } catch (error) {
      this.logDebug('ERROR: ' + error.message);
      console.error('ERROR in CoffeeShopScene.create():', error);
      throw error;
    }
  }
  
  createDebugLog(msg) {
    let el = document.getElementById('debug-log');
    if (!el) {
      el = document.createElement('div');
      el.id = 'debug-log';
      el.style.cssText = `
        position: fixed;
        top: 10px;
        left: 10px;
        right: 10px;
        max-height: 300px;
        overflow-y: auto;
        background: rgba(0,0,0,0.9);
        color: #0f0;
        font-family: monospace;
        font-size: 12px;
        padding: 10px;
        z-index: 10000;
        border: 2px solid #0f0;
      `;
      document.body.appendChild(el);
    }
    this.debugLogEl = el;
    el.innerHTML = msg + '<br>';
    console.log(msg);
  }
  
  logDebug(msg) {
    if (this.debugLogEl) {
      this.debugLogEl.innerHTML += msg + '<br>';
      this.debugLogEl.scrollTop = this.debugLogEl.scrollHeight;
    }
    console.log(msg);
  }
  
  drawBackground() {
    // First-person workspace view
    // Back wall (warm cafe color)
    const g = this.add.graphics();
    g.fillStyle(0x8B7355);
    g.fillRect(0, 0, 800, 300);
    
    // Shelves/decor on back wall
    g.fillStyle(0x5D4037);
    g.fillRect(50, 80, 700, 8);
    g.fillRect(50, 180, 700, 8);
    
    // Window view (left side)
    g.fillStyle(0x87CEEB);
    g.fillRect(20, 40, 150, 120);
    g.fillStyle(0x3E2723);
    g.lineStyle(4, 0x3E2723);
    g.strokeRect(20, 40, 150, 120);
  }
  
  drawCoffeeShop() {
    // First-person view: Draw the workspace/counter in front of us
    const g = this.add.graphics();
    
    // === BACK WALL ===
    // Base color
    g.fillStyle(0x8B6914);
    g.fillRect(0, 0, 800, 360);
    
    // Brick pattern
    for (let y = 0; y < 360; y += 24) {
      for (let x = (y % 48 === 0 ? 0 : -24); x < 820; x += 48) {
        // Brick
        g.fillStyle(Phaser.Display.Color.GetColor(
          139 + Math.random() * 20 - 10,
          90 + Math.random() * 20 - 10,
          43 + Math.random() * 10 - 5
        ));
        g.fillRect(x + 1, y + 1, 46, 22);
        
        // Highlight
        g.fillStyle(0x9B7924);
        g.fillRect(x + 2, y + 2, 44, 2);
        
        // Shadow
        g.fillStyle(0x6B4904);
        g.fillRect(x + 1, y + 20, 46, 3);
      }
    }
    
    // === WINDOW ===
    this.drawWindow(580, 50, 180, 160);
    
    // === MENU BOARD ===
    this.drawMenuBoard(80, 60);
    
    // === SHELVES WITH ITEMS ===
    this.drawShelf(260, 80, 200);
    this.drawShelf(260, 180, 200);
    
    // === COUNTER ===
    // Counter front (wood grain)
    g.fillStyle(0x5D4037);
    g.fillRect(0, 340, 800, 80);
    
    // Wood grain lines
    for (let i = 0; i < 10; i++) {
      g.fillStyle(Phaser.Display.Color.GetColor(73 + i * 3, 48 + i * 2, 40 + i));
      g.fillRect(0, 345 + i * 8, 800, 4);
    }
    
    // Counter top (polished wood)
    g.fillStyle(0x8B5A2B);
    g.fillRect(0, 330, 800, 15);
    g.fillStyle(0xA0522D);
    g.fillRect(0, 330, 800, 3);
    g.fillStyle(0x6B4226);
    g.fillRect(0, 342, 800, 3);
    
    // Counter edge highlight
    g.fillStyle(0xCD853F);
    g.fillRect(0, 330, 800, 2);
    
    // === FLOOR ===
    // Checkered tile pattern
    for (let y = 420; y < 600; y += 40) {
      for (let x = 0; x < 800; x += 40) {
        const isLight = ((x / 40) + (y / 40)) % 2 === 0;
        g.fillStyle(isLight ? 0x4A3520 : 0x3D2914);
        g.fillRect(x, y, 40, 40);
        
        // Tile highlight
        g.fillStyle(isLight ? 0x5A4530 : 0x4D3924);
        g.fillRect(x + 1, y + 1, 38, 2);
        
        // Tile shadow
        g.fillStyle(isLight ? 0x3A2510 : 0x2D1904);
        g.fillRect(x + 1, y + 37, 38, 2);
      }
    }
    
    // === DECORATIONS ===
    // Hanging lights
    this.drawHangingLight(200, 20);
    this.drawHangingLight(400, 30);
    this.drawHangingLight(600, 20);
    
    // Plants
    this.drawPlant(30, 300);
    this.drawPlant(740, 300);
    
    // Coffee machine equipment (behind counter)
    this.drawCoffeeMachine(500, 280);
    this.drawCoffeeMachine(300, 285);
    
    // Cups/mugs on counter
    this.drawCupStack(150, 335);
    this.drawCupStack(650, 335);
    
    // === SIGN ===
    this.drawSign(400, 25);
  }
  
  drawWindow(x, y, w, h) {
    const g = this.add.graphics();
    
    // Window frame (dark wood)
    g.fillStyle(0x3E2723);
    g.fillRect(x - 8, y - 8, w + 16, h + 16);
    
    // Sky/outside
    g.fillStyle(0x87CEEB);
    g.fillRect(x, y, w, h);
    
    // Clouds
    g.fillStyle(0xFFFFFF);
    g.fillCircle(x + 40, y + 40, 20);
    g.fillCircle(x + 60, y + 35, 25);
    g.fillCircle(x + 80, y + 45, 18);
    g.fillCircle(x + 130, y + 70, 22);
    g.fillCircle(x + 150, y + 65, 18);
    
    // Sun
    g.fillStyle(0xFFD700);
    g.fillCircle(x + w - 30, y + 30, 20);
    
    // Window panes
    g.fillStyle(0x3E2723);
    g.fillRect(x + w/2 - 3, y, 6, h);
    g.fillRect(x, y + h/2 - 3, w, 6);
    
    // Window sill
    g.fillStyle(0x5D4037);
    g.fillRect(x - 10, y + h, w + 20, 12);
    g.fillStyle(0x8D6E63);
    g.fillRect(x - 10, y + h, w + 20, 3);
    
    // Small plant on sill
    this.add.text(x + w/2, y + h + 2, '🌱', { fontSize: '16px' }).setOrigin(0.5, 0);
  }
  
  drawMenuBoard(x, y) {
    const g = this.add.graphics();
    
    // Board frame
    g.fillStyle(0x3E2723);
    g.fillRect(x - 5, y - 5, 150, 130);
    
    // Chalkboard
    g.fillStyle(0x1B1B1B);
    g.fillRect(x, y, 140, 120);
    
    // Chalk texture
    g.fillStyle(0x2B2B2B);
    for (let i = 0; i < 20; i++) {
      g.fillRect(x + Math.random() * 130, y + Math.random() * 110, 3, 3);
    }
    
    // Menu text
    this.add.text(x + 70, y + 12, 'MENU', {
      fontSize: '14px',
      fill: '#F5DEB3',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    
    this.add.text(x + 10, y + 30, [
      'Espresso      $3.50',
      'Americano    $4.00', 
      'Latte            $5.50',
      'Cappuccino  $5.50',
      'Mocha          $6.00'
    ].join('\n'), {
      fontSize: '10px',
      fill: '#FFFFFF',
      lineSpacing: 4
    });
  }
  
  drawShelf(x, y, w) {
    const g = this.add.graphics();
    
    // Shelf bracket
    g.fillStyle(0x3E2723);
    g.fillRect(x, y + 25, 8, 15);
    g.fillRect(x + w - 8, y + 25, 8, 15);
    
    // Shelf
    g.fillStyle(0x5D4037);
    g.fillRect(x, y + 20, w, 8);
    g.fillStyle(0x8D6E63);
    g.fillRect(x, y + 20, w, 2);
    
    // Items on shelf
    const items = ['📦', '🫙', '☕', '🍵', '📦'];
    items.forEach((item, i) => {
      this.add.text(x + 20 + i * 38, y + 8, item, { fontSize: '18px' }).setOrigin(0.5, 1);
    });
  }
  
  drawHangingLight(x, y) {
    const g = this.add.graphics();
    
    // Wire
    g.lineStyle(2, 0x333333);
    g.lineBetween(x, 0, x, y + 30);
    
    // Lamp shade
    g.fillStyle(0x2D2D2D);
    g.fillTriangle(x - 20, y + 30, x + 20, y + 30, x, y + 10);
    
    // Bulb glow
    g.fillStyle(0xFFF8DC, 0.3);
    g.fillCircle(x, y + 40, 30);
    
    // Bulb
    g.fillStyle(0xFFE4B5);
    g.fillCircle(x, y + 35, 8);
  }
  
  drawPlant(x, y) {
    const g = this.add.graphics();
    
    // Pot
    g.fillStyle(0x8B4513);
    g.fillRect(x - 15, y + 20, 30, 25);
    g.fillStyle(0xA0522D);
    g.fillRect(x - 18, y + 15, 36, 8);
    
    // Plant
    this.add.text(x, y, '🌿', { fontSize: '36px' }).setOrigin(0.5, 1);
  }
  
  drawCoffeeMachine(x, y) {
    const g = this.add.graphics();
    
    // Machine body
    g.fillStyle(0x2C2C2C);
    g.fillRoundedRect(x - 30, y - 50, 60, 80, 5);
    
    // Chrome top
    g.fillStyle(0x888888);
    g.fillRect(x - 28, y - 48, 56, 8);
    
    // Display
    g.fillStyle(0x001a00);
    g.fillRect(x - 20, y - 35, 40, 15);
    g.fillStyle(0x00FF00);
    g.fillRect(x - 18, y - 33, 36, 2);
    
    // Buttons
    g.fillStyle(0x444444);
    g.fillCircle(x - 10, y - 10, 5);
    g.fillCircle(x + 10, y - 10, 5);
    
    // Drip tray
    g.fillStyle(0x555555);
    g.fillRect(x - 25, y + 15, 50, 12);
    
    // Steam
    for (let i = 0; i < 3; i++) {
      const steam = this.add.text(x - 8 + i * 8, y - 55, '~', {
        fontSize: '12px',
        fill: '#FFFFFF',
        alpha: 0.4
      });
      this.tweens.add({
        targets: steam,
        y: y - 80,
        alpha: 0,
        duration: 2000 + i * 200,
        delay: i * 300,
        repeat: -1
      });
    }
  }
  
  drawCupStack(x, y) {
    const g = this.add.graphics();
    
    // Stack of cups
    for (let i = 0; i < 3; i++) {
      g.fillStyle(0xFFFFF0);
      g.fillRect(x - 8 + i * 4, y - 15 - i * 8, 16, 12);
      g.fillStyle(0xF5F5DC);
      g.fillRect(x - 6 + i * 4, y - 13 - i * 8, 12, 8);
    }
  }
  
  drawSign(x, y) {
    const g = this.add.graphics();
    
    // Sign board
    g.fillStyle(0x2C1810);
    g.fillRoundedRect(x - 140, y - 15, 280, 45, 8);
    
    // Inner board
    g.fillStyle(0x3D2817);
    g.fillRoundedRect(x - 135, y - 10, 270, 35, 5);
    
    // Text with glow effect
    this.add.text(x, y + 8, '☀️ SUNRISE COFFEE ☀️', {
      fontSize: '22px',
      fill: '#FFD700',
      fontStyle: 'bold',
      stroke: '#8B4513',
      strokeThickness: 2
    }).setOrigin(0.5);
  }
  
  drawPlayer(x, y) {
    const container = this.add.container(x, y);
    const g = this.add.graphics();
    container.add(g);
    
    // Shadow
    g.fillStyle(0x000000, 0.2);
    g.fillEllipse(0, 45, 50, 15);
    
    // Body
    g.fillStyle(0x4A6FA5); // Nice blue shirt
    g.fillRoundedRect(-18, -25, 36, 55, 8);
    
    // Shirt details
    g.fillStyle(0x3A5F95);
    g.fillRect(-2, -20, 4, 50); // Button line
    
    // Apron
    g.fillStyle(0x2D2D2D);
    g.beginPath();
    g.moveTo(-16, -5);
    g.lineTo(16, -5);
    g.lineTo(14, 30);
    g.lineTo(-14, 30);
    g.closePath();
    g.fill();
    
    // Apron pocket
    g.fillStyle(0x3D3D3D);
    g.fillRect(-8, 8, 16, 12);
    
    // Apron strings
    g.lineStyle(2, 0x2D2D2D);
    g.lineBetween(-16, -5, -22, 0);
    g.lineBetween(16, -5, 22, 0);
    
    // Arms
    g.fillStyle(0x4A6FA5);
    g.fillEllipse(-25, 5, 10, 18);
    g.fillEllipse(25, 5, 10, 18);
    
    // Hands (resting on counter)
    g.fillStyle(0xE8BEAC);
    g.fillCircle(-28, 18, 7);
    g.fillCircle(28, 18, 7);
    
    // Neck
    g.fillStyle(0xE8BEAC);
    g.fillRect(-6, -32, 12, 10);
    
    // Head
    g.fillStyle(0xE8BEAC);
    g.fillCircle(0, -45, 18);
    
    // Hair
    g.fillStyle(0x4A3728);
    g.beginPath();
    g.arc(0, -50, 18, Math.PI, 0, false);
    g.fill();
    g.fillRect(-18, -55, 8, 15);
    g.fillRect(10, -55, 8, 15);
    
    // Eyes
    g.fillStyle(0xFFFFFF);
    g.fillCircle(-6, -47, 4);
    g.fillCircle(6, -47, 4);
    g.fillStyle(0x4A3728);
    g.fillCircle(-6, -47, 2);
    g.fillCircle(6, -47, 2);
    
    // Eyebrows
    g.lineStyle(2, 0x3A2718);
    g.lineBetween(-10, -54, -3, -53);
    g.lineBetween(3, -53, 10, -54);
    
    // Nose
    g.fillStyle(0xD4A88C);
    g.fillTriangle(0, -44, -3, -38, 3, -38);
    
    // Smile
    g.lineStyle(2, 0x8B5A5A);
    g.beginPath();
    g.arc(0, -38, 6, 0.3, Math.PI - 0.3, false);
    g.strokePath();
    
    // Name tag
    g.fillStyle(0xFFFFFF);
    g.fillRoundedRect(-20, 0, 40, 14, 3);
    g.fillStyle(0x4A6FA5);
    g.fillRect(-18, 0, 36, 4);
    
    const nameTag = this.add.text(0, 8, window.playerName || 'You', {
      fontSize: '8px',
      fill: '#333'
    }).setOrigin(0.5);
    container.add(nameTag);
    
    container.setDepth(10);
  }
  
  createAmbientEffects() {
    // Floating dust particles
    for (let i = 0; i < 15; i++) {
      const dust = this.add.circle(
        Phaser.Math.Between(0, 800),
        Phaser.Math.Between(100, 400),
        Phaser.Math.Between(1, 2),
        0xFFFFFF,
        0.3
      );
      
      this.tweens.add({
        targets: dust,
        x: dust.x + Phaser.Math.Between(-50, 50),
        y: dust.y + Phaser.Math.Between(-30, 30),
        alpha: { from: 0.3, to: 0.1 },
        duration: Phaser.Math.Between(3000, 6000),
        yoyo: true,
        repeat: -1,
        ease: 'Sine.easeInOut'
      });
    }
  }
  
  createUI() {
    // Semi-transparent UI bar at bottom
    const uiBg = this.add.graphics();
    uiBg.fillStyle(0x000000, 0.5);
    uiBg.fillRect(0, 550, 800, 50);
    
    // Instructions
    this.add.text(400, 575, 'SPACE talk  •  C make coffee', {
      fontSize: '13px',
      fill: '#888'
    }).setOrigin(0.5);
    
    // Day/time with icon
    this.timeText = this.add.text(15, 565, '☀️ Day 1 • Morning', {
      fontSize: '14px',
      fill: '#FFD700'
    });
    
    // Money with icon
    this.moneyText = this.add.text(785, 565, '💰 $0.00', {
      fontSize: '15px',
      fill: '#4ADE80'
    }).setOrigin(1, 0);
    
    // Customer status
    this.customerText = this.add.text(400, 540, '', {
      fontSize: '12px',
      fill: '#aaa',
      backgroundColor: 'rgba(0,0,0,0.5)',
      padding: { x: 8, y: 4 }
    }).setOrigin(0.5).setDepth(50);
  }
  
  update() {
    if (window.dialogueManager?.currentNpc || this.coffeeGame?.isActive) {
      if (Phaser.Input.Keyboard.JustDown(this.escKey)) {
        window.dialogueManager?.closeDialogue();
      }
      return;
    }
    
    // Spawn customers (max 4 in queue)
    if (this.time.now > this.nextSpawnTime && this.npcsAtCounter.length < 4) {
      this.spawnCustomer();
      this.nextSpawnTime = this.time.now + Phaser.Math.Between(10000, 18000);
    }
    
    // Get the current customer (first in queue)
    const currentCustomer = this.npcsAtCounter.find(npc => npc.queuePosition === 0);
    
    if (currentCustomer && !currentCustomer.served) {
      // Talk to current customer
      if (Phaser.Input.Keyboard.JustDown(this.spaceKey)) {
        window.dialogueManager.startConversation(currentCustomer, this.gameState);
      }
      
      // Make coffee for current customer
      if (Phaser.Input.Keyboard.JustDown(this.cKey) && currentCustomer.hasOrdered) {
        this.coffeeGame.startOrder(currentCustomer.order);
        this.currentServingNpc = currentCustomer;
      }
    }
    
    // Update status text
    if (this.npcsAtCounter.length === 0) {
      this.customerText.setText('☕ Waiting for customers...');
    } else if (currentCustomer) {
      const queueCount = this.npcsAtCounter.length - 1;
      const queueText = queueCount > 0 ? ` (${queueCount} waiting)` : '';
      this.customerText.setText(`Serving: ${currentCustomer.name}${queueText}`);
    }
    
    this.updateSelection();
  }
  
  talkToSelectedCustomer() {
    const currentCustomer = this.npcsAtCounter.find(npc => npc.queuePosition === 0);
    if (currentCustomer && !currentCustomer.served && window.dialogueManager) {
      console.log('[Mobile] Starting conversation with', currentCustomer.name);
      if (window.updateStatus) window.updateStatus('Talking to ' + currentCustomer.name);
      window.dialogueManager.startConversation(currentCustomer, this.gameState);
    } else {
      console.log('[Mobile] No customer to talk to');
      if (window.updateStatus) window.updateStatus('No customer available');
    }
  }
  
  startCoffeeMaking() {
    const currentCustomer = this.npcsAtCounter.find(npc => npc.queuePosition === 0);
    if (currentCustomer && currentCustomer.hasOrdered && !currentCustomer.served) {
      console.log('[Mobile] Starting coffee for', currentCustomer.name, '- order:', currentCustomer.order);
      if (window.updateStatus) window.updateStatus('Making ' + currentCustomer.order);
      this.coffeeGame.startOrder(currentCustomer.order);
      this.currentServingNpc = currentCustomer;
    } else {
      console.log('[Mobile] Cannot make coffee - no valid customer');
      if (window.updateStatus) window.updateStatus('No order to make');
    }
  }
  
  spawnCustomer() {
    // First-person: Only show one customer at a time
    if (this.npcsAtCounter.length > 0) return;
    
    if (window.updateStatus) window.updateStatus('Customer arrived! Tap TALK');
    
    const npcData = this.getRandomNpc();
    
    // First-person: customer appears at counter facing you
    const container = drawCustomerFirstPerson(this, npcData);
    container.setAlpha(0); // Start invisible
    
    const npc = {
      ...npcData,
      container: container,
      queuePosition: 0,
      hasOrdered: false,
      served: false,
      order: this.getRandomOrder(),
      patience: 100
    };
    
    // Fade in animation
    this.tweens.add({
      targets: container,
      alpha: 1,
      duration: 800,
      ease: 'Power1',
      onComplete: () => {
        npc.hasOrdered = true;
        this.showOrderBubble(npc);
      }
    });
    
    this.npcsAtCounter.push(npc);
    this.updateSelection();
  }
  
  createNpcSprite(container, npcData) {
    const g = this.add.graphics();
    container.add(g);
    
    const color = npcData.color;
    const darkColor = Phaser.Display.Color.ValueToColor(color).darken(20).color;
    
    // Shadow
    g.fillStyle(0x000000, 0.2);
    g.fillEllipse(0, 35, 40, 12);
    
    // Body
    g.fillStyle(color);
    g.fillRoundedRect(-15, -20, 30, 45, 6);
    
    // Body shading
    g.fillStyle(darkColor);
    g.fillRect(-13, -15, 6, 40);
    
    // Head
    g.fillStyle(0xE8BEAC);
    g.fillCircle(0, -35, 15);
    
    // Hair (varies by NPC)
    g.fillStyle(npcData.hairColor || 0x4A3728);
    g.beginPath();
    g.arc(0, -40, 15, Math.PI, 0, false);
    g.fill();
    
    // Eyes
    g.fillStyle(0xFFFFFF);
    g.fillCircle(-5, -37, 3);
    g.fillCircle(5, -37, 3);
    g.fillStyle(0x333333);
    g.fillCircle(-5, -37, 1.5);
    g.fillCircle(5, -37, 1.5);
    
    // Smile
    g.lineStyle(1.5, 0x8B5A5A);
    g.beginPath();
    g.arc(0, -32, 4, 0.3, Math.PI - 0.3, false);
    g.strokePath();
    
    // Name
    const nameText = this.add.text(0, -58, npcData.name, {
      fontSize: '11px',
      fill: '#FFF',
      stroke: '#000',
      strokeThickness: 3
    }).setOrigin(0.5);
    container.add(nameText);
    
    container.setDepth(20);
  }
  
  showOrderBubble(npc) {
    // First-person: bubble appears above their head
    const bubble = this.add.container(0, -120);
    npc.container.add(bubble);
    
    const g = this.add.graphics();
    bubble.add(g);
    
    // Bubble (pointing left toward NPC)
    g.fillStyle(0xFFFFFF);
    g.fillRoundedRect(-5, -22, 95, 44, 10);
    
    // Pointer pointing LEFT
    g.fillTriangle(-5, 0, -15, -8, -15, 8);
    
    // Shadow
    g.fillStyle(0x000000, 0.1);
    g.fillRoundedRect(-3, -20, 95, 44, 10);
    
    const drinkEmoji = {
      espresso: '☕',
      americano: '☕',
      latte: '🥛',
      cappuccino: '☕',
      mocha: '🍫',
      macchiato: '🍮'
    };
    
    const orderText = this.add.text(42, 0, `${drinkEmoji[npc.order] || '☕'} ${npc.order}`, {
      fontSize: '12px',
      fill: '#333',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    bubble.add(orderText);
    
    npc.orderBubble = bubble;
    
    bubble.setScale(0);
    this.tweens.add({
      targets: bubble,
      scale: 1,
      duration: 300,
      ease: 'Back.easeOut'
    });
  }
  
  updateSelection() {
    // Only highlight the current customer (first in queue)
    const currentCustomer = this.npcsAtCounter.find(npc => npc.queuePosition === 0);
    
    if (!currentCustomer || currentCustomer.served) {
      this.selectionArrow.setVisible(false);
      return;
    }
    
    this.selectionArrow.setPosition(currentCustomer.container.x, currentCustomer.container.y - 100);
    this.selectionArrow.setVisible(true);
  }
  
  onCoffeeComplete(result) {
    const npc = this.currentServingNpc;
    if (!npc) return;
    
    npc.served = true;
    this.gameState.money += result.total;
    this.gameState.totalCoffeesMade++;
    this.moneyText.setText(`💰 $${this.gameState.money.toFixed(2)}`);
    
    // Remove order bubble
    if (npc.orderBubble) {
      npc.orderBubble.destroy();
    }
    
    // Thank you
    const thanksBubble = this.add.container(0, -80);
    npc.container.add(thanksBubble);
    
    const g = this.add.graphics();
    thanksBubble.add(g);
    g.fillStyle(0xFFFFFF);
    g.fillRoundedRect(-40, -18, 80, 36, 8);
    g.fillTriangle(0, 18, -6, 10, 6, 10);
    
    const emoji = result.qualityScore >= 80 ? '😊' : result.qualityScore >= 50 ? '🙂' : '😐';
    const msg = result.qualityScore >= 80 ? `${emoji} Thanks!` : result.qualityScore >= 50 ? `${emoji} Thanks` : `${emoji} ...ok`;
    
    const text = this.add.text(0, 0, msg, {
      fontSize: '12px',
      fill: '#333',
      fontStyle: 'bold'
    }).setOrigin(0.5);
    thanksBubble.add(text);
    
    // Money float
    const moneyFloat = this.add.text(400, 280, `+$${result.total.toFixed(2)}`, {
      fontSize: '28px',
      fill: '#4ADE80',
      fontStyle: 'bold',
      stroke: '#000',
      strokeThickness: 3
    }).setOrigin(0.5);
    
    this.tweens.add({
      targets: moneyFloat,
      y: 220,
      alpha: 0,
      duration: 1500,
      ease: 'Power2',
      onComplete: () => moneyFloat.destroy()
    });
    
    // Leave after delay
    this.time.delayedCall(2000, () => {
      this.customerLeaves(npc, thanksBubble);
    });
  }
  
  customerLeaves(npc, bubble) {
    // First-person: fade out
    this.tweens.add({
      targets: npc.container,
      alpha: 0,
      duration: 800,
      ease: 'Power1',
      onComplete: () => {
        npc.container.destroy();
        const idx = this.npcsAtCounter.indexOf(npc);
        if (idx > -1) this.npcsAtCounter.splice(idx, 1);
        
        this.updateSelection();
        
        // Spawn next customer after a brief delay
        this.nextSpawnTime = this.time.now + 3000;
      }
    });
  }
  
  advanceQueue() {
    // First-person: no queue to advance, handled by spawn timing
  }
  
  getRandomNpc() {
    const npcs = [
      { id: 'maya', name: 'Maya Chen', color: 0xE8B86D, hairColor: 0x1A1A1A, hairStyle: 'medium',
        system_prompt: `You are Maya Chen, a cheerful 28-year-old coworker. You're optimistic and have a slight crush on ${window.playerName || 'the barista'}. Keep responses to 1-2 sentences.` },
      { id: 'james', name: 'James Morrison', color: 0x5D6D7E, hairColor: 0x4A4A4A, hairStyle: 'short',
        system_prompt: `You are James Morrison, the 45-year-old manager. Professional but warm. Checking in briefly. 1-2 sentences.` },
      { id: 'alex', name: 'Alex Rivera', color: 0x6495ED, hairColor: 0x8B4513, hairStyle: 'short', hasGlasses: true,
        system_prompt: `You are Alex Rivera, a tired office worker. Polite but not chatty. You just want your coffee. Very brief responses.` },
      { id: 'sam', name: 'Sam Taylor', color: 0x98FB98, hairColor: 0x2F4F4F, hairStyle: 'medium',
        system_prompt: `You are Sam Taylor, a friendly college student. Caffeinated and chatty but keep it brief since you're ordering.` },
      { id: 'jordan', name: 'Jordan Blake', color: 0xDDA0DD, hairColor: 0x800080, hairStyle: 'long',
        system_prompt: `You are Jordan Blake, a creative type. Friendly and observant. Sometimes you notice odd things about ${window.playerName || 'the barista'}. Brief responses.` }
    ];
    return npcs[Phaser.Math.Between(0, npcs.length - 1)];
  }
  
  getRandomOrder() {
    return ['espresso', 'americano', 'latte', 'cappuccino'][Phaser.Math.Between(0, 3)];
  }
  
  updateTimeDisplay() {
    const { currentDay, timeOfDay } = this.gameState;
    const icon = timeOfDay === 'morning' ? '☀️' : timeOfDay === 'afternoon' ? '🌤️' : '🌙';
    this.timeText.setText(`${icon} Day ${currentDay} • ${timeOfDay}`);
  }
}
